<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateLocale } from 'dashboard/composables/useTimeAgo';
import MessageFunnel from 'flow_builder/components/reports/MessageFunnel.vue';
import WeekdayHourHeatmap from 'flow_builder/components/reports/WeekdayHourHeatmap.vue';
import ReportCards from './ReportCards.vue';

const props = defineProps({
  report: { type: Object, default: null },
  loading: { type: Boolean, default: false },
});

const { t } = useI18n();
const { localeTag } = useDateLocale();
const percentFormatter = new Intl.NumberFormat(localeTag.value, {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const rateCards = computed(() => [
  {
    key: 'delivery',
    label: t('CAMPAIGN.REPORTS.DELIVERY.DELIVERY_RATE'),
    value: percentFormatter.format(props.report?.rates?.delivery ?? 0),
    description: t('CAMPAIGN.REPORTS.DELIVERY.DELIVERY_RATE_DESC'),
  },
  {
    key: 'open',
    label: t('CAMPAIGN.REPORTS.DELIVERY.OPEN_RATE'),
    value: percentFormatter.format(props.report?.rates?.open ?? 0),
    description: t('CAMPAIGN.REPORTS.DELIVERY.OPEN_RATE_DESC'),
  },
  {
    key: 'failure',
    label: t('CAMPAIGN.REPORTS.DELIVERY.FAILURE_RATE'),
    value: percentFormatter.format(props.report?.rates?.failure ?? 0),
    description: t('CAMPAIGN.REPORTS.DELIVERY.FAILURE_RATE_DESC'),
  },
]);

const funnel = computed(() => {
  const f = props.report?.funnel ?? {};
  return {
    sent: f.sent ?? 0,
    delivered: f.delivered ?? 0,
    read: f.read ?? 0,
    failed: f.failed ?? 0,
  };
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <div
      class="border border-n-slate-4 bg-n-solid-1 rounded-lg p-4 flex flex-col gap-3"
    >
      <span class="text-sm font-semibold text-n-slate-12">
        {{ t('CAMPAIGN.REPORTS.DELIVERY.FUNNEL_TITLE') }}
      </span>
      <div v-if="loading" class="flex flex-col gap-3">
        <div
          v-for="n in 4"
          :key="`funnel-${n}`"
          class="h-8 rounded-lg bg-n-alpha-2 animate-pulse"
        />
      </div>
      <MessageFunnel v-else :funnel="funnel" />
    </div>

    <ReportCards
      :cards="rateCards"
      :loading="loading"
      :columns="3"
      :skeleton-count="3"
    />

    <div
      class="border border-n-slate-4 bg-n-solid-1 rounded-lg p-4 flex flex-col gap-3"
    >
      <span class="text-sm font-semibold text-n-slate-12">
        {{ t('CAMPAIGN.REPORTS.DELIVERY.HEATMAP_TITLE') }}
      </span>
      <div v-if="loading" class="h-56 rounded-md bg-n-alpha-2 animate-pulse" />
      <WeekdayHourHeatmap v-else :grid="report?.weekday_hour_heatmap ?? []" />
    </div>
  </div>
</template>
